import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DaycareService } from '../services/daycareService';
import { 
    transformCheckinFormData, 
    transformCheckoutFormData,
    getDogsAvailableForCheckin,
    getVisitsAvailableForCheckout
} from '../utils/daycareUtils';

/**
 * Custom hook for managing daycare visit operations
 * Encapsulates visit-related state and business logic
 */
export function useDaycareVisits(selectedDate = null) {
    const { authorizedFetch } = useAuth();
    
    // State
    const [visits, setVisits] = useState([]);
    const [openPreviousVisits, setOpenPreviousVisits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [warnings, setWarnings] = useState([]);

    /**
     * Load visits from previous days that were never checked out. Nothing closes
     * these automatically, so the board has to show them until staff do.
     */
    const loadOpenPreviousVisits = async () => {
        try {
            const stale = await DaycareService.getOpenPreviousVisits(authorizedFetch);
            setOpenPreviousVisits(Array.isArray(stale) ? stale : []);
        } catch (err) {
            // A missing-checkout reminder is not worth failing the board over
            console.error('Failed to load open visits from previous days:', err);
        }
    };

    /**
     * Load today's visits from API
     */
    const loadTodaysVisits = async (date = selectedDate) => {
        setLoading(true);
        setError('');
        try {
            const visitsData = await DaycareService.getTodaysVisits(authorizedFetch, date);
            setVisits(visitsData);
        } catch (err) {
            console.error('Failed to load today\'s visits:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Check in a dog
     * @param {Object} formData - Check-in form data { dogId, notes }
     */
    const checkinDog = async (formData) => {
        setError('');
        setSuccess('');

        if (!formData.dogId) {
            setError('Please select a dog');
            throw new Error('Dog selection required');
        }

        setLoading(true);
        try {
            const visitData = transformCheckinFormData(formData);
            const createdVisit = await DaycareService.createVisit(visitData, authorizedFetch);

            if (createdVisit.warnings?.length > 0) {
                setWarnings(createdVisit.warnings);
            }

            setSuccess('Dog checked in successfully!');
            await loadTodaysVisits(); // Refresh the list

            return createdVisit;
        } catch (err) {
            console.error('Failed to check in dog:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Check out a dog
     * @param {string} visitId - Visit ID
     * @param {string|Object} notesOrObj - Checkout notes string or { notes, checkoutMood, checkoutBehaviorNotes }
     */
    const checkoutDog = async (visitId, notesOrObj = '') => {
        setError('');
        setSuccess('');
        
        if (!visitId) {
            setError('Please select a visit to check out');
            throw new Error('Visit selection required');
        }
        
        try {
            const visitData = transformCheckoutFormData(notesOrObj);
            const updatedVisit = await DaycareService.updateVisit(visitId, visitData, authorizedFetch);
            
            setSuccess('Dog checked out successfully!');
            await loadTodaysVisits(); // Refresh the list
            await loadOpenPreviousVisits(); // A closed visit drops off the reminder

            return updatedVisit;
        } catch (err) {
            console.error('Failed to check out dog:', err);
            setError(err.message);
            throw err;
        }
    };

    /**
     * Get dogs available for check-in
     * @param {Array} allDogs - All dogs in the system
     * @returns {Array} Dogs available for check-in
     */
    const getAvailableDogsForCheckin = (allDogs) => {
        return getDogsAvailableForCheckin(allDogs, visits);
    };

    /**
     * Get visits available for check-out
     * @returns {Array} Visits available for check-out
     */
    const getAvailableVisitsForCheckout = () => {
        return getVisitsAvailableForCheckout(visits);
    };

    /**
     * Clear messages
     */
    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    const clearWarnings = () => setWarnings([]);

    /**
     * Initialize data loading
     */
    useEffect(() => {
        loadTodaysVisits(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        loadOpenPreviousVisits();
    }, []);

    return {
        visits,
        openPreviousVisits,
        loadOpenPreviousVisits,
        loading,
        error,
        success,
        warnings,
        loadTodaysVisits,
        checkinDog,
        checkoutDog,
        getAvailableDogsForCheckin,
        getAvailableVisitsForCheckout,
        clearMessages,
        clearWarnings,
    };
}
